// Read-only podglad pozycji kalkulacji widoczny w karcie "Upsale" rozwinietego wiersza
// pozycji oferty. Pokazuje co podbija cene bez koniecznosci wchodzenia w modal.
// Edycja/usuwanie/dodawanie pozostaje w `CalculationPositionsModal` (otwieranym linkiem
// "Pokaz kalkulacje" pod nazwa produktu).

import React, { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';

import { useGetSalesCrmOfferCalculationPositions } from 'api';

import styles from 'theme/components/containers/TraderClientOfferProductsContainer/TraderClientOfferProductsContainer.module.scss';

const formatPrice = (raw?: number | null): string => {
  if (raw == null || Number.isNaN(raw)) return '0,00 zł';
  return `${raw.toFixed(2).replace('.', ',')} zł`;
};

interface IPositionCalculationSummaryProps {
  clientId: number;
  offerId: string;
  calculationId: number;
  calculationName: string;
  /** Skrót jednostki pozycji oferty (np. "szt.", "wpal"). Używany w kolumnie "Typ" przy składnikach UNIT. */
  unit?: string | null;
  /** Czy nad listą wyświetlić separator (true gdy są też produkty upsell lub usługi). */
  showDivider?: boolean;
}

const PositionCalculationSummary: FC<IPositionCalculationSummaryProps> = ({
  clientId,
  offerId,
  calculationId,
  calculationName,
  unit,
  showDivider = false
}) => {
  const { t } = useTranslation();

  const { data, isLoading, isError } = useGetSalesCrmOfferCalculationPositions(
    clientId,
    offerId,
    calculationId,
    { enabled: calculationId > 0 }
  );

  const items = data?.items ?? [];
  // Sumy netto/brutto liczone po stronie backendu (RecalculateOfferVersionCalculationTotalsService z Money).
  // Frontend wyswietla — nie liczy. Fallback do sumy z items tylko gdy backend nie zwrocil totals
  // (stare odpowiedzi z cache lub nieprzeliczone kalkulacje).
  const totalNet = data?.totals?.valueNet
    ?? items.reduce((sum, row) => sum + (row.valueNet ?? 0), 0);

  // Suma kosztów własnych (netto). Pomijamy pozycje bez `costNet` (null/undefined).
  const totalCostNet = items.reduce(
    (sum, row) => sum + (row.costNet ?? 0),
    0
  );
  const hasAnyCost = items.some((row) => row.costNet !== null && row.costNet !== undefined);

  if (isLoading) {
    return (
      <div className={showDivider ? styles.calculationSummaryWithDivider : styles.calculationSummary}>
        <CircularProgress size={16} />
      </div>
    );
  }

  if (isError || items.length === 0) {
    return null;
  }

  return (
    <div className={showDivider ? styles.calculationSummaryWithDivider : styles.calculationSummary}>
      <div className={styles.calculationSummaryHeader}>
        {t('salesCrm.calculation.summary.title', { defaultValue: 'Kalkulacja' })}: {calculationName}
      </div>
      <div
        className={
          hasAnyCost ? styles.calculationSummaryTable : styles.calculationSummaryTableNoCost
        }>
        <div className={styles.calculationSummaryTableHead}>
          <span>
            {t('salesCrm.calculation.summary.colName', { defaultValue: 'Składnik' })}
          </span>
          <span>
            {t('salesCrm.calculation.summary.colType', { defaultValue: 'Typ' })}
          </span>
          <span className={styles.calculationSummaryNumeric}>
            {t('salesCrm.calculation.summary.colPrice', { defaultValue: 'Cena' })}
          </span>
          {hasAnyCost && (
            <span className={styles.calculationSummaryNumeric}>
              {t('salesCrm.calculation.summary.colCost', { defaultValue: 'Koszt' })}
            </span>
          )}
        </div>
        {items.map((row) => {
          // Pozycje kalkulacji nie maja juz `quantity` — finalna ilosc pochodzi z pozycji oferty.
          // Etykieta "/ <jednostka>" pokazuje, ze cena bedzie mnozona przez qty pozycji oferty
          // ("matryca" doliczana raz / "nadruk" mnozony przez ilosc w jednostce pozycji).
          const perUnitLabel = unit && unit.trim() !== ''
            ? `/ ${unit}`
            : t('salesCrm.calculation.summary.perUnit', { defaultValue: '/ szt.' });
          const typeLabel = row.priceType === 'UNIT'
            ? perUnitLabel
            : t('salesCrm.calculation.summary.once', { defaultValue: 'jednorazowo' });
          return (
            <div key={row.id} className={styles.calculationSummaryRow}>
              <span className={styles.calculationSummaryName}>{row.name}</span>
              <span className={styles.calculationSummaryMeta}>{typeLabel}</span>
              <span className={styles.calculationSummaryNumeric}>{formatPrice(row.priceNet)}</span>
              {hasAnyCost && (
                <span className={styles.calculationSummaryNumericMuted}>
                  {row.costNet !== null && row.costNet !== undefined
                    ? formatPrice(row.costNet)
                    : '—'}
                </span>
              )}
            </div>
          );
        })}
      </div>
      {hasAnyCost && (
        <div className={styles.calculationSummaryTotal}>
          <span>
            {t('salesCrm.calculation.summary.costTotal', { defaultValue: 'Suma kosztów zakupu (netto)' })}:
          </span>
          <strong>{formatPrice(totalCostNet)}</strong>
        </div>
      )}
      <div className={styles.calculationSummaryTotal}>
        <span>{t('salesCrm.calculation.summary.total', { defaultValue: 'Suma kalkulacji (netto)' })}:</span>
        <strong>{formatPrice(totalNet)}</strong>
      </div>
    </div>
  );
};

export default PositionCalculationSummary;
